'use client';

import { Endpoints } from '@/lib/Endpoints';
import { Requester } from '@/lib/Requester';
import { GenericResponse } from '@/models/GenericResponse';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';

type VideoMessageLinkResponse = {
  link?: string;
};

export default function VideoMessageViewerDialog({
  videoKey,
  open,
  onOpenChange,
}: {
  videoKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const safeVideoKey = useMemo(() => videoKey?.trim(), [videoKey]);

  useEffect(() => {
    if (!open) return;
    if (!safeVideoKey) {
      setError('Video anahtarı bulunamadı.');
      return;
    }

    let cancelled = false;
    const loadLink = async () => {
      setIsLoading(true);
      setError(null);
      setVideoUrl(null);

      try {
        const { data: result } = await Requester.get<GenericResponse<VideoMessageLinkResponse>>(
          Endpoints.GET_VIDEO_MESSAGE_LINK,
          { params: { videoKey: safeVideoKey } },
        );

        if (cancelled) return;

        if (result?.meta?.statusCode !== 0) {
          setError(result?.meta?.message || 'Video mesaj getirilemedi.');
          return;
        }

        const link = result?.data?.link;
        if (!link) {
          setError('Video linki bulunamadı.');
          return;
        }

        setVideoUrl(link);
      } catch (e) {
        if (cancelled) return;
        setError('Video mesaj getirilemedi.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadLink();

    return () => {
      cancelled = true;
    };
  }, [open, safeVideoKey]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTitle className="hidden">Video Mesaj</DialogTitle>
      <DialogContent className="sm:max-w-3xl w-[92vw] max-h-[85vh] p-0 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-hzc_border_secondary bg-white">
          <div className="text-base font-semibold text-hzc_beige_900">Video Mesaj</div>
          <Button
            variant="ghost"
            className="h-8 px-3 text-sm text-hzc_beige_700 hover:text-hzc_beige_900"
            onClick={() => onOpenChange(false)}
          >
            Kapat
          </Button>
        </div>

        <div className="bg-black/5 p-3 md:p-4 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12 md:py-16">
              <Spinner className="size-12" />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <p className="text-sm text-hzc_beige_800">{error}</p>
              <Button
                className="rounded-full bg-hzc_green hover:bg-hzc_green_dark text-hzc_purple_500"
                onClick={() => onOpenChange(false)}
              >
                Kapat
              </Button>
            </div>
          )}

          {!isLoading && !error && videoUrl && (
            <video
              controls
              playsInline
              className="w-full max-h-[52vh] sm:max-h-[70vh] rounded-xl bg-black"
              src={videoUrl}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
